import { useEffect, useMemo, useRef, useState } from "react";
import type { Key } from "react-aria-components";
import type { ComboboxSelectionMode } from "./combobox.js";
import { comboboxCreateKey, readItemKey } from "./comboboxItems.js";
import { isExplicitRemoval, isPointerRemoval } from "./comboboxRemoval.js";
import { warnOnce } from "./devWarning.js";

export { comboboxCreateKey } from "./comboboxItems.js";

export interface ComboboxCreateOption {
  id: typeof comboboxCreateKey;
  /** Ready to render, e.g. `Add "kiwi"`. */
  label: string;
}

type ComboboxValue<M extends ComboboxSelectionMode> = M extends "single"
  ? Key | null
  : Key[];

export interface UseCreatableComboboxOptions<
  T extends object,
  M extends ComboboxSelectionMode = "single",
> {
  /**
   * The real options, without any "Add ..." entry. Memoize it (and the
   * callbacks below) to keep react-aria from rebuilding its collection on every
   * render. Keyed by `key`, then `id` — key the option objects themselves, not
   * the rendered `ComboboxItem`: an `id` set there wins in react-aria's
   * collection but is invisible here, which leaves every key unlabelled.
   */
  items: readonly T[];
  /**
   * The text an option shows. Compared case-insensitively against the typed
   * text, so an exact match offers the option itself instead of "Add ...".
   */
  getLabel: (item: T) => string;
  value: ComboboxValue<M>;
  onChange: (value: ComboboxValue<M>) => void;
  /**
   * Called with the trimmed typed text when the user picks "Add ...". Add the
   * new option to `items` and return its key: it is selected in the same
   * update, so the caller never has to select it itself.
   */
  onCreate: (label: string) => Key;
  selectionMode?: M;
  /** Overrides the "Add ..." label. Keep the typed text in it, or the built-in filter will hide the option. */
  formatCreateLabel?: (label: string) => string;
}

export interface UseCreatableComboboxResult<
  T extends object,
  M extends ComboboxSelectionMode = "single",
> {
  /**
   * Spread onto {@link Combobox}. Supplies the options (including "Add ..."),
   * the selection, and the controlled input text.
   */
  comboboxProps: {
    defaultItems: readonly (T | ComboboxCreateOption)[];
    value: ComboboxValue<M>;
    onChange: (value: ComboboxValue<M>) => void;
    inputValue: string;
    onInputChange: (inputValue: string) => void;
    selectionMode?: M;
  };
}

const isSameKeys = (a: readonly Key[], b: readonly Key[]) =>
  a.length === b.length && a.every((key, index) => key === b[index]);

// A module constant, not a default parameter: a fresh identity every render
// would rebuild the option list — and with it react-aria's whole collection —
// on every keystroke, which is what the `items` doc above warns callers about.
const defaultFormatCreateLabel = (label: string) => `Add "${label}"`;

/**
 * Adds an "Add ..." option to a {@link Combobox} so the typed text can become a
 * new option, and owns the input text that the flow needs (see the controlled
 * usage rules on {@link Combobox}).
 *
 * The caller keeps owning the data: `onCreate` adds the option and returns its
 * key, which this hook then selects.
 *
 * ```tsx
 * const [options, setOptions] = useState(fruits);
 * const [value, setValue] = useState<Key[]>([]);
 * const { comboboxProps } = useCreatableCombobox({
 *   items: options,
 *   getLabel: (fruit) => fruit.label,
 *   value,
 *   onChange: setValue,
 *   selectionMode: "multiple",
 *   onCreate: (label) => {
 *     setOptions((current) => [...current, { id: label, label }]);
 *     return label;
 *   },
 * });
 * ```
 *
 * For the common case — a multi-select of free-form strings — reach for
 * {@link CreatableComboboxField} instead, which wires this up for you.
 */
export function useCreatableCombobox<
  T extends object,
  M extends ComboboxSelectionMode = "single",
>(
  options: UseCreatableComboboxOptions<T, M>,
): UseCreatableComboboxResult<T, M> {
  const {
    items,
    getLabel,
    value,
    onChange,
    onCreate,
    selectionMode,
    formatCreateLabel = defaultFormatCreateLabel,
  } = options;

  // Same silent miswiring ComboboxChips warns about: options keyed somewhere
  // this can't read leave every label unresolved, which shows up as an empty
  // input and an "Add ..." offered for a value that already exists.
  const hasUnkeyedItem = items.some((item) => readItemKey(item) === undefined);
  useEffect(() => {
    if (hasUnkeyedItem) {
      warnOnce(
        "useCreatableCombobox reads an option's key off the item object (`key`, then `id`), and one of these has neither. Key the objects themselves — an `id` on the rendered ComboboxItem is invisible here.",
      );
    }
  }, [hasUnkeyedItem]);

  const labelOfKey = (key: Key): string | undefined => {
    const item = items.find((candidate) => readItemKey(candidate) === key);
    return item ? getLabel(item) : undefined;
  };

  // Seeded from the selection, because controlling both the value and the input
  // text stops react-aria from doing it (react-stately's `useComboBoxState`
  // skips `resetInputValue` when both are controlled).
  const [inputValue, setInputValue] = useState(() =>
    Array.isArray(value) || value === null ? "" : (labelOfKey(value) ?? ""),
  );
  const typedLabel = inputValue.trim();

  // Undefined until the first effect run, so mounting counts as a change and
  // the two branches below agree about the initial selection.
  const lastValue = useRef<ComboboxValue<M> | undefined>(undefined);
  // Set while a selected key has no label yet, because its option reaches
  // `items` later than the selection does (an async load).
  const isAwaitingLabel = useRef(false);

  // Follow the selection when it changes from the outside (a form reset, a
  // clear button), and fill in its label if that arrives afterwards.
  useEffect(() => {
    if (Array.isArray(value)) {
      return;
    }
    const hasChanged = value !== lastValue.current;
    lastValue.current = value;

    if (value === null) {
      if (hasChanged) {
        setInputValue("");
      }
      isAwaitingLabel.current = false;
      return;
    }

    const label = labelOfKey(value);
    if (hasChanged) {
      isAwaitingLabel.current = label === undefined;
      if (label !== undefined) {
        setInputValue(label);
      }
      return;
    }

    // The label landed after the selection. Only ever fills an empty input: a
    // just-created option also reaches `items` a render late, and its text is
    // already there — as is anything the user has since typed.
    if (isAwaitingLabel.current && label !== undefined && inputValue === "") {
      isAwaitingLabel.current = false;
      setInputValue(label);
    }
    // labelOfKey closes over items/getLabel, which are read fresh each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items, inputValue]);

  // An option claiming the reserved key wins over the synthetic one: real data
  // is never shadowed, hidden from its chip, or dropped as if it were the
  // "Add ..." entry. The cost is that nothing new can be created while such an
  // option is around — a fair trade for a key the docs reserve.
  const isCreateKeyTaken = useMemo(
    () => items.some((item) => readItemKey(item) === comboboxCreateKey),
    [items],
  );
  const isCreateKey = (key: Key) =>
    key === comboboxCreateKey && !isCreateKeyTaken;

  const optionsWithCreate = useMemo<
    readonly (T | ComboboxCreateOption)[]
  >(() => {
    const hasExactMatch = items.some(
      (item) => getLabel(item).toLowerCase() === typedLabel.toLowerCase(),
    );
    if (typedLabel === "" || hasExactMatch || isCreateKeyTaken) {
      return items;
    }

    // Last, so the first real match stays the target of Enter while typing.
    return [
      ...items,
      { id: comboboxCreateKey, label: formatCreateLabel(typedLabel) },
    ];
  }, [items, typedLabel, getLabel, formatCreateLabel, isCreateKeyTaken]);

  const handleChange = (next: ComboboxValue<M>) => {
    if (Array.isArray(next)) {
      // The conditional value type only narrows to `any[]`, so name the element
      // type here rather than letting it spread through the checks below.
      const current: readonly Key[] = Array.isArray(value) ? value : [];
      const keys: Key[] = next.filter((key) => !isCreateKey(key));
      const isCreating = keys.length !== next.length && typedLabel !== "";

      if (isCreating) {
        const createdKey = onCreate(typedLabel);
        if (!keys.includes(createdKey)) {
          keys.push(createdKey);
        }
      }

      // react-aria commits the value on blur, Escape and popup close, whether
      // or not anything changed; forwarding those hands the caller a new array
      // that reads as an edit. The search is over eitherway, so it clears —
      // leaving typed text that produced no chip would read as a pending value
      // and be lost on submit.
      if (!isCreating && isSameKeys(keys, current)) {
        setInputValue("");
        return;
      }

      // Typing the label of something already chosen and confirming it reads as
      // "add this" — but the only matching option is the one already selected,
      // so react-aria toggles it off. Treat it as a no-op that ends the search
      // instead of a removal. Never for a removal the user asked for by name:
      // that one means what it says even when the filter spells it out.
      const removed = current.filter((key) => !keys.includes(key));
      const isConfirmingExisting =
        !isCreating &&
        !isExplicitRemoval() &&
        // Nor for a press on the option itself: its checkmark advertises a
        // toggle, and pressing it can't mean anything but "remove this".
        !isPointerRemoval() &&
        typedLabel !== "" &&
        removed.length > 0 &&
        removed.every(
          (key) =>
            (labelOfKey(key) ?? String(key)).toLowerCase() ===
            typedLabel.toLowerCase(),
        );
      if (isConfirmingExisting) {
        setInputValue("");
        return;
      }

      // A fresh pick starts a fresh search; a removal leaves the filter text
      // alone, since the user is likely still typing it.
      if (keys.length > current.length) {
        setInputValue("");
      }
      onChange(keys as ComboboxValue<M>);
      return;
    }

    if (next !== null && isCreateKey(next)) {
      if (typedLabel === "") {
        return;
      }
      const createdKey = onCreate(typedLabel);
      lastValue.current = createdKey as ComboboxValue<M>;
      setInputValue(typedLabel);
      onChange(createdKey as ComboboxValue<M>);
      return;
    }

    // Controlling both the value and the input text means react-aria stops
    // syncing them, so show the selected option's label here.
    lastValue.current = next;
    setInputValue(next === null ? "" : (labelOfKey(next) ?? ""));
    if (next !== value) {
      onChange(next);
    }
  };

  return {
    comboboxProps: {
      defaultItems: optionsWithCreate,
      value,
      onChange: handleChange,
      inputValue,
      onInputChange: setInputValue,
      selectionMode,
    },
  };
}
